/**
 * Carte KPI pour afficher une métrique avec tendance
 */

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: number;
  trendLabel?: string;
  icon?: React.ReactNode;
  color?: 'blue' | 'green' | 'orange' | 'red' | 'purple';
  loading?: boolean;
}

const colorClasses = {
  blue: 'bg-blue-50 text-blue-600 border-blue-200',
  green: 'bg-green-50 text-green-600 border-green-200',
  orange: 'bg-orange-50 text-orange-600 border-orange-200',
  red: 'bg-red-50 text-red-600 border-red-200',
  purple: 'bg-purple-50 text-purple-600 border-purple-200',
};

export default function KPICard({
  title,
  value,
  subtitle,
  trend,
  trendLabel,
  icon,
  color = 'blue',
  loading = false,
}: KPICardProps) {
  const getTrendColor = (trend: number) => {
    if (trend > 0) return 'text-green-600';
    if (trend < 0) return 'text-red-600';
    return 'text-gray-500';
  };

  const getTrendIcon = (trend: number) => {
    if (trend > 0) return '↑';
    if (trend < 0) return '↓';
    return '→';
  };

  if (loading) {
    return (
      <div className={`rounded-lg border p-4 ${colorClasses[color]}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
          <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-1/3"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border p-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium opacity-80">{title}</span>
        {icon && <span className="text-xl">{icon}</span>}
      </div>

      <div className="text-2xl font-bold mb-1">{value}</div>

      <div className="flex items-center justify-between text-sm">
        {subtitle && <span className="opacity-70">{subtitle}</span>}

        {trend !== undefined && (
          <span className={`font-medium ${getTrendColor(trend)}`}>
            {getTrendIcon(trend)} {Math.abs(trend)}%{' '}
            {trendLabel && <span className="opacity-70">{trendLabel}</span>}
          </span>
        )}
      </div>
    </div>
  );
}
